import { useMutation } from "@apollo/client/react";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { captureException } from "@sentry/react";
import { isMCloudEmbedded } from "@src/utils/index";
import moment from "moment";

import { submitTestingSampleAudio } from "../../api/UploadAudio";
import callbackAudio from "../../assets/other/callback.wav";
import dentalRecordAudio from "../../assets/other/dental_record.wav";
import dictationAudio from "../../assets/other/dictation.wav";
import longMedicalRecordAudio from "../../assets/other/dobby-incomplete-extended.wav";
import medicalRecordAudio from "../../assets/other/medical_record.wav";
import multipetAudio from "../../assets/other/multipet.wav";
import packmodeAudio from "../../assets/other/packmode.wav";
import recapAudio from "../../assets/other/recap.wav";
import ultrasoundAudio from "../../assets/other/ultrasound.wav";
import { isTestEnv, testUsers, salesUsers } from "../../constants";
import { useLocalStorage, useTeam, useTemplates } from "../../hooks";
import { useTestUI } from "../../hooks/useTestUI";
import { alert } from "../common/Alert";
import { isUUID } from "../dashboard/utils";
import { DSButton, DSDropdown } from "../design-system/molecules";
import { CREATE_QUICK_NOTE } from "../notes/edit/api/mutations";

export const testRecordingsData = [
  {
    jobType: "medical_record",
    value: "medical_record",
    name: "Test Medical Record (SOAP)",
    audioUrl: medicalRecordAudio,
  },
  {
    jobType: "multipet",
    value: "multipet",
    name: "Test Multipet (SOAP)",
    audioUrl: multipetAudio,
  },
  {
    jobType: "packmode",
    value: "packmode",
    name: "Test Packmode (SOAP)",
    audioUrl: packmodeAudio,
  },
  {
    jobType: "medical_record",
    value: "long_medical_record",
    name: "Test Long Messy Medical Record",
    audioUrl: longMedicalRecordAudio,
  },
  {
    jobType: "dictation",
    value: "dictation",
    name: "Test Dictation",
    audioUrl: dictationAudio,
  },
  {
    jobType: "recap",
    value: "recap",
    name: "Test Recap",
    audioUrl: recapAudio,
  },
  {
    jobType: "callback",
    value: "callback",
    name: "Test Callback",
    audioUrl: callbackAudio,
  },
  {
    jobType: "dental_record",
    value: "dental_record",
    name: "Test Dental Record",
    audioUrl: dentalRecordAudio,
  },
  {
    jobType: "abdominal_ultrasound",
    value: "abdominal_ultrasound",
    name: "Test Ultrasound",
    audioUrl: ultrasoundAudio,
  },
];

const throwUnhandledError = () => {
  throw new Error("Unhandled test error from TestRecordings.js");
};

const throwHandledError = () => {
  try {
    throw new Error("Handled test error from TestRecordings.js");
  } catch (error) {
    captureException(error);
  }
};

export const SubmitTestRecordingButton = ({
  onClick,
  text = "Submit Selected",
}) => {
  return (
    <DSButton
      variant="secondary"
      size="sm"
      onClick={onClick}
      dataTestId="submit-test-recording-button"
    >
      {text}
    </DSButton>
  );
};

export const TestRecordTypeDropdown = ({
  selectedTestRecordType,
  setSelectedTestRecordType,
}) => {
  const { allTemplates, loadingTemplates } = useTemplates();

  const favoriteTemplates = allTemplates.filter(
    (template) => template.isFavourite,
  );

  const allOptions = [
    ...testRecordingsData.map((r) => ({
      label: r.name,
      value: r.value,
    })),
    ...(favoriteTemplates.length > 0
      ? [
          {
            label: "Favorite Templates",
            value: "favorites-header",
            isHeader: true,
            icon: faStar,
          },
          ...favoriteTemplates.map((t) => ({
            label: t.name,
            value: t.uuid,
          })),
        ]
      : []),
  ];

  return (
    <DSDropdown
      options={allOptions}
      value={selectedTestRecordType}
      onChange={(value) => setSelectedTestRecordType(value)}
      search={true}
      searchPlaceholder="Search recordings..."
      placeholder="Select recording type"
      isOptionDisabled={(opt) => opt.isHeader}
      disabled={loadingTemplates}
      width="w-[250px]"
      dataTestId="test-recording-type-dropdown"
    />
  );
};

export function TestRecordings({ data }) {
  const [createQuickNote] = useMutation(CREATE_QUICK_NOTE);
  const { isTestUIEnabled } = useTestUI();

  const [selectedTestRecordType, setSelectedTestRecordType] =
    useLocalStorage("testrecord-type", "medical_record");

  const { currentTeam } = useTeam();

  const createNewNote = async (
    audioStorageLink,
    storageObjectName,
  ) => {
    let jobType = JSON.parse(localStorage.getItem("testrecord-type"));

    return await createQuickNote({
      variables: {
        noteInput: {
          audioStorageLink: audioStorageLink,
          storageObjectName: storageObjectName,
          createdAt: moment().format(),
          lastUpdatedAt: moment().format(),
          jobType: isUUID(jobType) ? "template" : jobType,
          teamUuid: currentTeam?.uuid,
          ...(isUUID(jobType) && {
            templateUuid: jobType,
          }),
        },
      },
    }).then(() => {
      alert("success", "Recording has been uploaded!");
    });
  };

  const submitTestRecording = () => {
    let recording = testRecordingsData.find(
      (recording) => recording.value === selectedTestRecordType,
    );
    let jobType;
    let audioUrl;

    if (recording) {
      // Scribenote defaults
      jobType = recording.jobType;
      audioUrl = recording.audioUrl;
    } else {
      // Adaptive templates
      jobType = selectedTestRecordType;
      audioUrl = medicalRecordAudio;
    }

    localStorage.setItem("testrecord-type", JSON.stringify(jobType));

    submitTestingSampleAudio({
      createNewNote,
      audioUrl,
    });
  };

  const isARealUserInRealEnv =
    !isTestEnv && !testUsers.includes(data?.user?.email);

  const isASalesUser = salesUsers.includes(data?.user?.email);

  if (isMCloudEmbedded()) {
    return null;
  }

  if (isARealUserInRealEnv || isASalesUser) {
    return null;
  }

  return (
    <div>
      {isTestUIEnabled && (
        <div className="flex flex-col">
          <div className="flex flex-row mt-2 space-x-2">
            <TestRecordTypeDropdown
              selectedTestRecordType={selectedTestRecordType}
              setSelectedTestRecordType={setSelectedTestRecordType}
            />
            <SubmitTestRecordingButton
              onClick={submitTestRecording}
            />
            <DSButton
              variant="secondary"
              size="sm"
              onClick={throwHandledError}
              dataTestId="throw-handled-error-button"
            >
              Throw Handled Error
            </DSButton>
            <DSButton
              variant="secondary"
              size="sm"
              onClick={throwUnhandledError}
              dataTestId="throw-unhandled-error-button"
            >
              Throw Unhandled Error
            </DSButton>
          </div>
          <span className="text-xs text-secondary font-italic">
            You can show/hide the test UI by clicking on the footer.
          </span>
        </div>
      )}
    </div>
  );
}
